import { Select, SelectProps } from 'antd';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useState, useMemo } from 'react';
import InfiniteSelect from '@/components/_common/InfiniteSelect';
import { useDebounce } from '@/hooks';
import { ListCompaniesQuery } from '@/core/domain/enterprise/Companies/Companies.types';
import { useAreas } from '@/core/domain/operational/Areas/Areas.hooks';
import { SubArea } from '@/core/domain/operational/SubAreas/SubAreas.types';

type Props = Omit<SelectProps, 'options'> & {
  queryFilter?: ListCompaniesQuery;
  isListArea?: boolean;
  subAreaSelected?: SubArea;
};

const AreasInfiniteSelectField: React.FC<Props> = ({
  queryFilter,
  isListArea,
  subAreaSelected,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState<string>();
  const search = useDebounce(searchValue);

  const {
    data: areasData,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useAreas({
    ...queryFilter,
    active: true,
    rows: 30,
    areaOrSubAreaName: search,
  });

  const areas = useMemo(() => flatInfiniteData(areasData), [areasData]);

  const areasWithInactives = useMemo(() => {
    if (
      subAreaSelected &&
      !areas.some((area) =>
        area.subAreas.some((subArea) => subArea.id === subAreaSelected.id),
      )
    ) {
      return [
        ...areas,
        { id: 0, name: 'Inativos', subAreas: [subAreaSelected] },
      ];
    }
    return areas;
  }, [areas, subAreaSelected]);

  const listAreaOptions = useMemo(() => {
    const areasToRender = areasWithInactives;
    return isListArea
      ? areasToRender.map((area) => (
          <Select.Option key={area.id} value={area.id}>
            {area.name}
          </Select.Option>
        ))
      : areasToRender.map((area) => (
          <Select.OptGroup key={area.id} label={area.name}>
            {area.subAreas.map((subArea) => (
              <Select.Option key={subArea.id} value={subArea.id}>
                {subArea.name}
              </Select.Option>
            ))}
          </Select.OptGroup>
        ));
  }, [isListArea, areasWithInactives]);

  return (
    <InfiniteSelect
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      loading={isLoading}
      children={listAreaOptions}
      showSearch={true}
      searchValue={searchValue}
      filterOption={false}
      onSearch={setSearchValue}
      {...props}
    />
  );
};

export default AreasInfiniteSelectField;
